import React from "react"
import { useState, useRef } from "react"
import { X, ImagePlus, Trash2 } from "lucide-react"
import { motion } from "framer-motion"
import styles from "./ProductPublicationModal.module.scss"
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"
import { fireAnalytics } from "../../../utils/firebase"
import { getDeviceCategory, getSessionId } from "../../../utils/analyticsHelpers.ts"

const modal = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 },
  },
}

interface ProductData {
  name: string
  description: string
  startDate: string
  endDate: string
  zone: string
  images: File[]
  type: string
  priceUnit: string
  price: string
  currencyType: string
}

interface ProductPublicationModalProps {
  onClose: () => void
  onBack: () => void
  onPublish: (data: ProductData) => void
  userData?: {
    id?: number
    image_profile?: string
    name?: string
    last_name?: string
    razon_social?: string
    type?: string
  } | null
}

export default function ProductPublicationModal({
  onClose,
  onBack,
  onPublish,
  userData,
}: ProductPublicationModalProps) {
  const [formData, setFormData] = useState<ProductData>({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    zone: "",
    images: [],
    type: "",
    priceUnit: "",
    price: "",
    currencyType: "",
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const fullName = userData?.type === "COMPANY" ? userData.razon_social : `${userData?.name ?? ""} ${userData?.last_name ?? ""}`.trim() || ""
  const { guard } = useActionGuard()
  const handleInputChange = (field: keyof ProductData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (formData.images.length + files.length > 5) {
      alert("No se pueden agregar más de 5 imágenes")
      return
    }
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }))
    setErrors((prev) => {
      const next = { ...prev }
      delete next.images
      return next
    })
  }

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }))
    setErrors((prev) => {
      const next = { ...prev }
      delete next.images
      return next
    })
  }

  const validateForm = (): Record<string, string> => {
    const nextErrors: Record<string, string> = {}
    if (!formData.name.trim()) nextErrors.name = "Este campo es obligatorio"
    if (!formData.description.trim()) nextErrors.description = "Este campo es obligatorio"
    if (!formData.startDate) nextErrors.startDate = "Este campo es obligatorio"
    if (!formData.endDate) nextErrors.endDate = "Este campo es obligatorio"
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      nextErrors.endDate = "La fecha fin debe ser posterior a la fecha de inicio"
    }
    if (!formData.zone.trim()) nextErrors.zone = "Este campo es obligatorio"
    if (formData.images.length === 0) nextErrors.images = "Debes agregar al menos una imagen"
    if (!formData.type) nextErrors.type = "Este campo es obligatorio"
    if (!formData.currencyType) nextErrors.currencyType = "Este campo es obligatorio"
    if (!formData.priceUnit) nextErrors.priceUnit = "Este campo es obligatorio"
    if (!formData.price.trim()) nextErrors.price = "Este campo es obligatorio"
    return nextErrors
  }

  const handlePublish = guard("create_product", () => {
    setIsSubmitting(true)
    const nextErrors = validateForm()
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      setIsSubmitting(false)
      const firstErrorKey = Object.keys(nextErrors)[0]
      const target = contentRef.current?.querySelector(`[data-field="${firstErrorKey}"]`)
      target?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }
    onPublish(formData)
  }, () => {
    fireAnalytics("product_created", {
      edify_user_id: userData?.id || null,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "publishing",
      action: "product_created",
      product_id: userData?.id || null,
    })
  })

    return (
      <ModalOverlay isOpen={true} onClose={onClose}>
        <motion.div
          variants={modal}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className={styles.modal}
        >
          <div className={styles.header}>
            <div className={styles.header_content}>
              <div className={styles.header_userInfo}>
                <img
                  src={userData?.image_profile || "/placeholder.svg?height=40&width=40"}
                  alt="profile"
                  className={styles.header_avatar}
                />
                <div>
                  <div className={styles.header_name}>{fullName}</div>
                  <div className={styles.header_subtitle}>Crear producto o servicio</div>
                </div>
              </div>
              <div className={styles.header_actions}>
                <button className={styles.header_button} onClick={onBack}>
                  ←
                </button>
                <button className={styles.header_button} onClick={onClose}>
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          <div className={styles.content} ref={contentRef}>
            <div className={`${styles.imageSection} ${errors.images ? styles.hasError : ""}`} data-field="images">
              {formData.images.length > 0 ? (
                <div className={styles.imageSection_grid}>
                  {formData.images.map((image, index) => (
                    <div key={index} className={styles.imageSection_item}>
                      <img
                        src={URL.createObjectURL(image) || "/placeholder.svg"}
                        alt={`Preview ${index + 1}`}
                        className={styles.imageSection_image}
                      />
                      <button className={styles.imageSection_removeButton} onClick={() => removeImage(index)}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                  {formData.images.length < 5 && (
                    <label className={styles.imageSection_addMore}>
                      <ImagePlus size={24} color="rgb(107, 114, 128)" />
                      <span>Agregar más</span>
                      <input type="file" accept="image/*" multiple onChange={handleImageChange} />
                    </label>
                  )}
                </div>
              ) : (
                <label className={styles.imageSection_empty}>
                  <ImagePlus size={48} color="rgb(156, 163, 175)" />
                  <div className={styles.imageSection_empty_title}>Agrega imágenes de tu producto o servicio</div>
                  <div className={styles.imageSection_empty_subtitle}>JPG, PNG hasta 5MB</div>
                  <div className={styles.imageSection_empty_button}>Seleccionar archivos</div>
                  <input type="file" accept="image/*" multiple onChange={handleImageChange} />
                </label>
              )}
              {errors.images && <span className={styles.formField_error}>{errors.images}</span>}
            </div>
            <div className={styles.formGrid}>
              <div className={`${styles.formField} ${errors.name ? styles.hasError : ""}`} data-field="name">
                <label htmlFor="name">Nombre *</label>
                <input
                  id="name"
                  type="text"
                  placeholder="Por ejemplo: Instalación de A/A"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                />
                {errors.name && <span className={styles.formField_error}>{errors.name}</span>}
              </div>

              <div className={`${styles.formField} ${errors.type ? styles.hasError : ""}`} data-field="type">
                <label htmlFor="type">Tipo *</label>
                <select id="type" value={formData.type} onChange={(e) => handleInputChange("type", e.target.value)}>
                  <option value="">Producto/servicio</option>
                  <option value="producto">Producto</option>
                  <option value="servicio">Servicio</option>
                </select>
                {errors.type && <span className={styles.formField_error}>{errors.type}</span>}
              </div>

              <div className={`${styles.formField} ${errors.zone ? styles.hasError : ""}`} data-field="zone">
                <label htmlFor="zone">Ubicación *</label>
                <input
                  id="zone"
                  type="text"
                  placeholder="Pilar, Buenos Aires"
                  value={formData.zone}
                  onChange={(e) => handleInputChange("zone", e.target.value)}
                />
                {errors.zone && <span className={styles.formField_error}>{errors.zone}</span>}
              </div>

              <div className={`${styles.formField} ${errors.currencyType ? styles.hasError : ""}`} data-field="currencyType">
                <label htmlFor="currencyType">Moneda *</label>
                <select
                  id="currencyType"
                  value={formData.currencyType}
                  onChange={(e) => handleInputChange("currencyType", e.target.value)}
                >
                  <option value="">ARS/USD/EUR</option>
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                {errors.currencyType && <span className={styles.formField_error}>{errors.currencyType}</span>}
              </div>

              <div className={`${styles.formField} ${errors.priceUnit ? styles.hasError : ""}`} data-field="priceUnit">
                <label htmlFor="priceUnit">Precio por *</label>
                <select
                  id="priceUnit"
                  value={formData.priceUnit}
                  onChange={(e) => handleInputChange("priceUnit", e.target.value)}
                >
                  <option value="">Precio por</option>
                  <option value="unidad">Unidad</option>
                  <option value="m">Metro</option>
                  <option value="m2">Metro cuadrado</option>
                  <option value="cm3">Centímetro cúbico</option>
                  <option value="hora">Hora</option>
                  <option value="trabajo completo">Trabajo completo</option>
                </select>
                {errors.priceUnit && <span className={styles.formField_error}>{errors.priceUnit}</span>}
              </div>

              <div className={`${styles.formField} ${errors.price ? styles.hasError : ""}`} data-field="price">
                <label htmlFor="price">Valor *</label>
                <input
                  id="price"
                  type="number"
                  placeholder="Ej: 100"
                  value={formData.price}
                  onChange={(e) => handleInputChange("price", e.target.value)}
                />
                {errors.price && <span className={styles.formField_error}>{errors.price}</span>}
              </div>

              <div className={`${styles.formField} ${errors.startDate ? styles.hasError : ""}`} data-field="startDate">
                <label htmlFor="startDate">Inicio *</label>
                <input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange("startDate", e.target.value)}
                />
                {errors.startDate && <span className={styles.formField_error}>{errors.startDate}</span>}
              </div>

              <div className={`${styles.formField} ${errors.endDate ? styles.hasError : ""}`} data-field="endDate">
                <label htmlFor="endDate">Fin *</label>
                <input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  min={formData.startDate}
                  onChange={(e) => handleInputChange("endDate", e.target.value)}
                />
                {errors.endDate && <span className={styles.formField_error}>{errors.endDate}</span>}
              </div>

              <div className={`${styles.formField} ${styles.fullWidth} ${errors.description ? styles.hasError : ""}`} data-field="description">
                <label htmlFor="description">Descripción *</label>
                <textarea
                  id="description"
                  placeholder="Ej: Instalación de 10 unidades de A/A en edificio de oficinas."
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                />
                {errors.description && <span className={styles.formField_error}>{errors.description}</span>}
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <button className={styles.cancelButton} onClick={onBack}>
              Cancelar
            </button>
            <button onClick={handlePublish} disabled={isSubmitting || Object.keys(errors).length > 0} className={styles.publishButton}>
              {Object.keys(errors).length > 0 ? "Faltan datos" : "Publicar"}
            </button>
          </div>
        </motion.div>
      </ModalOverlay>
  )
}
